import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { AttachmentsProvider } from './attachments-provider';
import { ChatAttachments } from './chat-attachments';
import { ChatAttachmentsPreview } from './chat-attachments-preview';
import { ChatInput } from './chat-input';
import { ChatInputProvider, DragHandlersProvider } from './chat-input-context';
import { ChatInputTextarea } from './chat-input-textarea';
import { ChatInputToolbar } from './chat-input-toolbar';
import { DragOverlay } from './drag-overlay';
import { SendButton } from './send-button';
import { useChatInputSetup } from './use-chat-input-setup';
import { WebSearchWarning } from './web-search-warning';
interface ChatInputContainerProps {
  placeholder?: string;
  className?: string;
  /**
   * Custom toolbar content. If provided, replaces the default toolbar.
   * If not provided, uses default toolbar with attachments and send button.
   */
  toolbar?: ReactNode;
}

/**
 * Default chat input container that sets up context and provides the standard composition.
 * Apps can customize the toolbar by passing a custom toolbar prop.
 *
 * For more advanced customization, use the `useChatInputSetup` hook directly.
 *
 * @example Default usage:
 * ```tsx
 * <ChatInputContainer />
 * ```
 *
 * @example Custom toolbar:
 * ```tsx
 * <ChatInputContainer
 *   toolbar={
 *     <ChatInputToolbar>
 *       <CustomButton />
 *       <SendButton />
 *     </ChatInputToolbar>
 *   }
 * />
 * ```
 *
 * @example Without attachments:
 * ```tsx
 * <ChatInputContainer
 *   toolbar={
 *     <ChatInputToolbar>
 *       <SendButton />
 *     </ChatInputToolbar>
 *   }
 * />
 * ```
 *
 * @example Full customization with hook:
 * ```tsx
 * function MyCustomInput() {
 *   const contextValue = useChatInputSetup();
 *
 *   return (
 *     <ChatInputProvider value={contextValue}>
 *       <ChatInput>
 *         <ChatInputTextarea />
 *         <ChatInputToolbar>
 *           <MyCustomActions />
 *           <SendButton />
 *         </ChatInputToolbar>
 *       </ChatInput>
 *     </ChatInputProvider>
 *   );
 * }
 * ```
 */
export function ChatInputContainer(t0) {
  const $ = _c(13);
  const {
    placeholder: t1,
    className,
    toolbar
  } = t0;
  const placeholder = t1 === undefined ? "How can I help you today?" : t1;
  const contextValue = useChatInputSetup();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChatInputToolbar><div className="flex flex-1"><ChatAttachments /></div><SendButton /></ChatInputToolbar>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const defaultToolbar = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DragOverlay />;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== placeholder) {
    t4 = <ChatInputTextarea placeholder={placeholder} />;
    $[2] = placeholder;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = toolbar ?? defaultToolbar;
  let t6;
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ChatAttachmentsPreview />;
    t7 = <WebSearchWarning />;
    $[4] = t6;
    $[5] = t7;
  } else {
    t6 = $[4];
    t7 = $[5];
  }
  let t8;
  if ($[6] !== className || $[7] !== t4 || $[8] !== t5) {
    t8 = <ChatInput className={className}>{t3}{t4}{t5}{t6}{t7}</ChatInput>;
    $[6] = className;
    $[7] = t4;
    $[8] = t5;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== contextValue || $[11] !== t8) {
    t9 = <AttachmentsProvider><DragHandlersProvider><ChatInputProvider value={contextValue}>{t8}</ChatInputProvider></DragHandlersProvider></AttachmentsProvider>;
    $[10] = contextValue;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  return t9;
}